var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useRef } from "react";
import { useDebounce } from "@ui/utils/useDebounce";
import { useSelector, useDispatch } from "react-redux";
import { getAllRoles, toggleRoleStatus, getAllRolesSummary, getRolesDropdown, } from "../../../../reducers/roles/thunk/role-thunk";
import { updateFilters, setGlobalSearch, } from "../../../../reducers/roles/slice/role-slice";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
export const useRolesFilter = (sortState, currentTab) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common"]);
    const { loading, paginationInfo, roleFilters, currentPageNumber, currentPageSize, rolesSummary, summaryLoading, } = useSelector((state) => state.role);
    const [localSearchTerm, setLocalSearchTerm] = useState(roleFilters.globalSearch || "");
    const { roles } = useSelector((state) => state.role);
    // Track previous values to prevent duplicate calls
    const prevQueryRef = useRef("");
    const hasInitialized = useRef(false);
    // Debounce the search term
    const debouncedSearchTerm = useDebounce(localSearchTerm, 500);
    // Update global search in Redux when debounced search term changes
    useEffect(() => {
        if (debouncedSearchTerm !== roleFilters.globalSearch) {
            dispatch(setGlobalSearch(debouncedSearchTerm || undefined));
        }
    }, [debouncedSearchTerm, dispatch]);
    // Fetch initial roles summary and all roles on mount
    useEffect(() => {
        if (!hasInitialized.current) {
            dispatch(getAllRolesSummary());
            hasInitialized.current = true;
        }
    }, [dispatch]);
    useEffect(() => {
        // Map frontend field to backend field (only if sortState exists)
        let sortColumn = sortState === null || sortState === void 0 ? void 0 : sortState.field;
        let sortDirection = sortState === null || sortState === void 0 ? void 0 : sortState.order;
        if (sortColumn === "name") {
            sortColumn = "name";
        }
        const query = {
            filters: roleFilters,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || undefined,
            sortDirection: sortDirection || undefined,
        };
        // Prevent duplicate calls by comparing query
        const queryString = JSON.stringify(query);
        if (queryString !== prevQueryRef.current) {
            prevQueryRef.current = queryString;
            dispatch(getAllRoles(query));
        }
    }, [currentPageNumber, currentPageSize, roleFilters, sortState]);
    const cleanFilterValues = (filters) => {
        const cleaned = {};
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            if (Array.isArray(value)) {
                cleaned[key] = value.length > 0 ? value : [];
            }
            else if (value === "" || value === null) {
                cleaned[key] = undefined;
            }
            else {
                cleaned[key] = value;
            }
        });
        return cleaned;
    };
    const handleFilterChange = (newFilters) => {
        const cleanedFilters = cleanFilterValues(newFilters);
        // Map 'name' field from array to first item if it's an array (from dropdown)
        if (Array.isArray(cleanedFilters.name) && cleanedFilters.name.length > 0) {
            cleanedFilters.name = cleanedFilters.name[0];
        }
        else if (Array.isArray(cleanedFilters.name) &&
            cleanedFilters.name.length === 0) {
            cleanedFilters.name = undefined;
        }
        dispatch(updateFilters(cleanedFilters));
    };
    const handleApplyFilters = () => {
        dispatch(getAllRolesSummary());
    };
    const handleSearchChange = (event) => {
        setLocalSearchTerm(event.target.value);
    };
    const handleClearFilters = () => {
        // Preserve isActive based on current tab
        let isActiveValue = undefined;
        if (currentTab === "active") {
            isActiveValue = true;
        }
        else if (currentTab === "inactive") {
            isActiveValue = false;
        }
        // Clear local search term first
        setLocalSearchTerm("");
        // Clear all filter fields
        dispatch(updateFilters({
            name: undefined,
            isActive: isActiveValue,
            isSystemRole: undefined,
            globalSearch: undefined,
        }));
    };
    const handleToggleActive = (role) => __awaiter(void 0, void 0, void 0, function* () {
        yield dispatch(toggleRoleStatus({ id: role.id, isActive: !role.isActive }))
            .unwrap()
            .then(() => {
            toast.success(t("common:toasts.roleStatusUpdatedSuccessfully"));
            // Refresh the roles list with current filters to reflect the status change
            let sortColumn = sortState === null || sortState === void 0 ? void 0 : sortState.field;
            let sortDirection = sortState === null || sortState === void 0 ? void 0 : sortState.order;
            if (sortColumn === "name") {
                sortColumn = "name";
            }
            const query = {
                filters: roleFilters,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize,
                sortColumn: sortColumn || undefined,
                sortDirection: sortDirection || undefined,
            };
            dispatch(getAllRoles(query));
            dispatch(getAllRolesSummary());
            // Refresh roles dropdown cache to reflect status change
            dispatch(getRolesDropdown());
        })
            .catch(() => {
            toast.error(t("common:toasts.failedToUpdateRoleStatus"));
        });
    });
    return {
        roles: roles || [],
        loading,
        searchTerm: localSearchTerm,
        filters: roleFilters,
        paginationInfo,
        rolesSummary: rolesSummary || [],
        summaryLoading,
        handleFilterChange,
        handleSearchChange,
        handleClearFilters,
        handleToggleActive,
        handleApplyFilters,
    };
};
